import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { buildBreadcrumbs } from "@/lib/structured-data";
import { Shield, Eye, Database, Lock, Mail, Phone } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen pt-28">
      <SEO
        title="Privacy Policy"
        canonicalUrl="/privacy"
        noIndex
        structuredData={buildBreadcrumbs([{ name: "Home", path: "/" }, { name: "Privacy Policy" }])}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 bg-wl-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-wl-green/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-wl-green" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-white/90">
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
          </p>
          <p className="text-sm text-white/70 mt-4">
            Last updated: July 2026
          </p>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Data Controller */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-wl-blue/10 rounded-lg flex items-center justify-center mr-4">
                <Database className="w-5 h-5 text-wl-blue" />
              </div>
              <h2 className="text-2xl font-bold text-wl-navy">Data Controller</h2>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <p className="text-wl-gray mb-4">
                <strong className="text-wl-navy">Whitefield Legal Services Limited</strong> is the data controller responsible for your personal information.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Registered Address:</strong></p>
                  <p>2nd Floor Argyle House<br />Joel Street, Northwood Hills<br />HA6 1NW</p>
                </div>
                <div>
                  <p><strong>Contact:</strong></p>
                  <p>Phone: 01923 620240/41<br />Email: admin@whitefieldlegal.com</p>
                  <p><strong>Company Number:</strong> 06043320</p>
                </div>
              </div>
            </div>
          </div>

          {/* Information We Collect */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-wl-green/10 rounded-lg flex items-center justify-center mr-4">
                <Eye className="w-5 h-5 text-wl-green" />
              </div>
              <h2 className="text-2xl font-bold text-wl-navy">Information We Collect</h2>
            </div>
            
            <div className="space-y-6">
              <div className="border-l-4 border-wl-blue pl-6">
                <h3 className="text-lg font-semibold text-wl-navy mb-2">Personal Information</h3>
                <ul className="text-wl-gray space-y-1">
                  <li>• Name and contact details (email, phone, address)</li>
                  <li>• Company information and professional details</li>
                  <li>• Property search requests and transaction details</li>
                  <li>• Communication preferences and history</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-wl-green pl-6">
                <h3 className="text-lg font-semibold text-wl-navy mb-2">Technical Information</h3>
                <ul className="text-wl-gray space-y-1">
                  <li>• IP address and browser information</li>
                  <li>• Website usage data and analytics</li>
                  <li>• Cookies and similar technologies</li>
                  <li>• Device and operating system information</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How We Use Information */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-wl-navy/10 rounded-lg flex items-center justify-center mr-4">
                <Lock className="w-5 h-5 text-wl-navy" />
              </div>
              <h2 className="text-2xl font-bold text-wl-navy">How We Use Your Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Service Delivery</h3>
                <ul className="text-wl-gray text-sm space-y-2">
                  <li>• Processing property search requests</li>
                  <li>• Providing conveyancing reports</li>
                  <li>• Managing client accounts and billing</li>
                  <li>• Customer support and communication</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-wl-navy mb-3">Business Operations</h3>
                <ul className="text-wl-gray text-sm space-y-2">
                  <li>• Improving our services and platform</li>
                  <li>• Security and fraud prevention</li>
                  <li>• Marketing and business development</li>
                  <li>• Legal and regulatory compliance</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Cookies & Analytics */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">Cookies &amp; Analytics</h2>
            <div className="bg-gray-50 p-6 rounded-xl space-y-4 text-wl-gray text-sm">
              <p>
                We use a small number of cookies and similar technologies. Cookies that are
                strictly necessary for the website to function are always active. Non-essential
                cookies &mdash; used for analytics that help us understand how visitors use the
                site &mdash; are only set <strong>after you accept them</strong> via our cookie
                banner. If you decline, no analytics cookies are placed and no analytics data is
                collected. You can change your choice at any time by clearing this site&apos;s
                cookies in your browser.
              </p>
              <p>
                Fonts on this website are hosted by us directly, so no data about your visit is
                sent to a third-party font provider.
              </p>
            </div>
          </div>

          {/* Third-Party Service Providers */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">Third-Party Service Providers</h2>
            <p className="text-wl-gray mb-6">
              We share personal data with the following trusted service providers (data
              processors) only as needed to operate this website and communicate with you. Each
              processes data on our behalf under a data-processing agreement:
            </p>
            <div className="space-y-4">
              <div className="border-l-4 border-wl-blue pl-6">
                <h3 className="text-lg font-semibold text-wl-navy mb-1">Brevo (email &amp; newsletter)</h3>
                <p className="text-wl-gray text-sm">
                  When you submit our contact form or subscribe to Property Pulse, your details
                  (such as name, email address, company, phone number and message) are processed
                  by Brevo to deliver our email correspondence and manage newsletter subscriptions.
                  Brevo stores this data within the EU.
                </p>
              </div>
              <div className="border-l-4 border-wl-green pl-6">
                <h3 className="text-lg font-semibold text-wl-navy mb-1">Google Analytics</h3>
                <p className="text-wl-gray text-sm">
                  If you accept analytics cookies, Google Analytics collects usage data such as
                  your IP address, device and browser information, and how you interact with the
                  site, to help us measure and improve performance. This runs only with your consent.
                </p>
              </div>
              <div className="border-l-4 border-wl-navy pl-6">
                <h3 className="text-lg font-semibold text-wl-navy mb-1">Vercel (hosting &amp; analytics)</h3>
                <p className="text-wl-gray text-sm">
                  This website is hosted on Vercel. If you accept analytics cookies, Vercel
                  Analytics and Speed Insights collect anonymised usage and performance data
                  (including IP-derived information) to help us monitor site speed and reliability.
                </p>
              </div>
            </div>
          </div>

          {/* International Data Transfers */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">International Data Transfers</h2>
            <div className="bg-wl-light p-6 rounded-xl">
              <p className="text-wl-gray text-sm">
                Some of our providers are based outside the UK and European Economic Area (EEA).
                In particular, Google Analytics and Vercel may process data in the United States.
                Where personal data is transferred outside the UK/EEA, we rely on appropriate
                safeguards &mdash; such as the UK International Data Transfer Agreement and the
                EU&ndash;US Data Privacy Framework &mdash; to ensure your data receives an
                equivalent level of protection. Brevo processes data within the EU, so no such
                transfer applies to your contact and newsletter data.
              </p>
            </div>
          </div>

          {/* Legal Basis */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">Legal Basis for Processing</h2>
            <div className="bg-wl-light p-6 rounded-xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                <div>
                  <h4 className="font-semibold text-wl-navy mb-2">Contract Performance</h4>
                  <p className="text-wl-gray">Processing necessary to fulfill our contractual obligations to provide property search services.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-wl-navy mb-2">Legitimate Interest</h4>
                  <p className="text-wl-gray">Business operations, security, and service improvement activities.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-wl-navy mb-2">Legal Obligation</h4>
                  <p className="text-wl-gray">Compliance with legal and regulatory requirements in the conveyancing industry.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">Your Rights Under GDPR</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: "Access", description: "Request copies of your personal data" },
                { title: "Rectification", description: "Request correction of inaccurate data" },
                { title: "Erasure", description: "Request deletion of your personal data" },
                { title: "Restriction", description: "Request restriction of processing" },
                { title: "Portability", description: "Request transfer of your data" },
                { title: "Objection", description: "Object to processing based on legitimate interests" }
              ].map((right, index) => (
                <div key={index} className="border border-gray-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-wl-navy mb-1">{right.title}</h4>
                  <p className="text-wl-gray text-sm">{right.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Data Retention */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">Data Retention</h2>
            <div className="bg-gray-50 p-6 rounded-xl">
              <p className="text-wl-gray mb-4">
                We retain personal data only for as long as necessary to fulfill the purposes outlined in this policy:
              </p>
              <ul className="text-wl-gray space-y-2">
                <li>• <strong>Client data:</strong> 7 years after last transaction (regulatory requirement)</li>
                <li>• <strong>Marketing data:</strong> Until consent is withdrawn</li>
                <li>• <strong>Website analytics:</strong> 26 months maximum</li>
                <li>• <strong>Support communications:</strong> 3 years from last contact</li>
              </ul>
            </div>
          </div>

          {/* Contact & Complaints */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-wl-navy mb-6">Contact Us</h2>
            <div className="bg-gray-50 p-6 rounded-xl">
              <p className="text-wl-gray mb-4">
                For any privacy-related questions or to exercise your rights, please contact us:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex items-center text-wl-navy">
                  <Mail className="w-4 h-4 mr-2" />
                  <span>admin@whitefieldlegal.com</span>
                </div>
                <div className="flex items-center text-wl-navy">
                  <Phone className="w-4 h-4 mr-2" />
                  <span>01923 620240/41</span>
                </div>
              </div>
              <p className="text-wl-gray text-sm">
                You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) 
                if you believe your data protection rights have been breached.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
} 